'use client';

import {
	MouseEvent as ReactMouseEvent,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { Menu } from '@drift-labs/icons';
import {
	TRADE_PAGE_CHART_DEFAULT_HEIGHT,
	TRADE_PAGE_CHART_HEIGHT_KEY,
} from 'src/constants/trade';
import { getColorVar } from 'src/utils/theme/colorVars';

const MAX_SIZE = 900;

const DraggableTableResizer = () => {
	const [tradePageChartHeight, setTradePageChartHeight] =
		useSyncLocalStorage<string>(TRADE_PAGE_CHART_HEIGHT_KEY);
	const [isResizing, setIsResizing] = useState(false);

	const tableStartSize = useRef<number>(0);
	const mouseYStart = useRef<number>(0);
	const mouseYChange = useRef<number>(0);

	const handleResizeEnd = () => {
		setIsResizing(false);
		mouseYStart.current = 0;
		mouseYChange.current = 0;
		window.removeEventListener('mouseup', handleResizeEnd);
		window.removeEventListener('mousemove', handleResizeMove);
	};

	const handleResizeStart = (event: ReactMouseEvent) => {
		if (!isResizing) {
			setIsResizing(true);
			const parsedHeight = tradePageChartHeight
				? parseInt(tradePageChartHeight, 10)
				: NaN;
			tableStartSize.current = Number.isNaN(parsedHeight)
				? TRADE_PAGE_CHART_DEFAULT_HEIGHT
				: parsedHeight;
			mouseYStart.current = event.clientY;
			mouseYChange.current = 0;
			window.addEventListener('mouseup', handleResizeEnd);
		}
	};

	const handleResizeMove = (event: MouseEvent) => {
		if (isResizing) {
			mouseYChange.current = event.clientY - mouseYStart.current;

			const newSize = Math.max(
				0,
				Math.min(MAX_SIZE, tableStartSize.current + mouseYChange.current)
			);

			setTradePageChartHeight(`${newSize}`);
		}
	};

	useEffect(() => {
		if (isResizing) {
			window.addEventListener('mousemove', handleResizeMove);
		}

		return () => window.removeEventListener('mousemove', handleResizeMove);
	}, [isResizing]);

	return (
		<div className="relative hidden h-0 w-full md:block">
			<div
				className="absolute -top-sm flex h-sm w-full cursor-ns-resize flex-row items-center justify-center select-none hover:bg-button-secondary-bg-hover"
				onMouseDown={handleResizeStart}
			>
				<Menu className="opacity-50" color={getColorVar('text-secondary')} />
			</div>
			{/* div to cover TV chart while resizing to prevent it from blocking window mousemove event */}
			<div
				className="absolute top-[-1504px] h-[3000px] w-full cursor-ns-resize"
				style={{ display: isResizing ? 'block' : 'none' }}
			></div>
		</div>
	);
};

export default DraggableTableResizer;
